import { FullPageLoader } from '@app/shared/components/full-page-loader';
import { PUBLIC_PATH } from '@app/shared/constants/paths.constant';
import { useAuthHydrated } from '@app/shared/hooks/use-auth-hydrated';
import { useAuthStore } from '@app/shared/store/auth.store';
import { useMenuStore } from '@app/shared/store/menu.store';
import {
  findFirstAccessibleLink,
  resolveAuthRedirect,
} from '@app/shared/utils/auth-redirect.utils';
import type { FC, ReactNode } from 'react';
import { Navigate } from 'react-router-dom';

interface GuestGuardProps {
  children: ReactNode;
}

export const GuestGuard: FC<GuestGuardProps> = ({ children }) => {
  const hasHydrated = useAuthHydrated();
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const user = useAuthStore((state) => state.user);
  const menu = useMenuStore((state) => state.menu);

  // Auth store hydrates asynchronously under Zustand v5. Rendering
  // the login page here before hydration would briefly flash /auth/*
  // to an already-authenticated operator on hard refresh.
  if (!hasHydrated) {
    return <FullPageLoader />;
  }

  if (isAuthenticated) {
    const target = resolveAuthRedirect(user, menu);
    // resolveAuthRedirect falls back to `/login` when the user has
    // no accessible menu items. Detect that case explicitly and
    // render the login surface instead of trying to navigate into
    // ourselves (would loop with AuthGuard).
    const firstLink = findFirstAccessibleLink(menu);
    if (target !== PUBLIC_PATH.LOGIN || firstLink) {
      return <Navigate to={target} />;
    }
    return children;
  }

  return children;
};

export default GuestGuard;
